/**
 * Med Access Requests Page – /traveler/med-access-requests
 *
 * Protected page (traveler must be logged in).
 * Shows all incoming doctor access requests and lets the traveler
 * approve or deny them.
 */
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";
import {
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  RefreshCw,
  Stethoscope,
  User,
  XCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";

interface MedAccessRequest {
  id: string;
  token: string;
  expires_at: string;
  consent_status: "PENDING" | "GRANTED" | "DENIED" | "REVOKED";
  doctor_name?: string;
  doctor_email?: string;
  accessed_at?: string;
  created_at: string;
}

export const MedAccessRequestsPage: React.FC = () => {
  const { t } = useI18nStore();
  const dateFmt = useLocaleFormat();
  const [requests, setRequests] = useState<MedAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/med-access/list");
      setRequests(res.data.data ?? []);
    } catch {
      setError(t.cardxReqLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const respond = async (token: string, action: "GRANTED" | "DENIED") => {
    setActing((prev) => ({ ...prev, [token]: true }));
    try {
      await apiClient.put(`/med-access/${token}/consent`, { action });
      setRequests((prev) =>
        prev.map((r) =>
          r.token === token ? { ...r, consent_status: action } : r,
        ),
      );
    } catch {
      setError(t.cardxReqUpdateFailed);
    } finally {
      setActing((prev) => ({ ...prev, [token]: false }));
    }
  };

  const fmt = (iso: string) => dateFmt.dateTime(iso, "medium");

  const pending = requests.filter(
    (r) => r.consent_status === "PENDING" && !r.doctor_name,
  );
  const active = requests.filter(
    (r) => r.consent_status === "PENDING" && !!r.doctor_name,
  );
  const resolved = requests.filter((r) => r.consent_status !== "PENDING");

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t.cardxReqTitle}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t.cardxReqSubtitle}</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t.refresh}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin" />
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Stethoscope className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t.cardxReqEmpty}</p>
          <p className="text-gray-400 text-xs mt-1">{t.cardxReqEmptyHint}</p>
        </div>
      )}

      {/* Awaiting doctor (token shared but not yet claimed) */}
      {pending.length > 0 && (
        <Section title={t.cardxReqSectionAwaitingDoctor} count={pending.length}>
          {pending.map((r) => (
            <RequestCard key={r.id} r={r} fmt={fmt} t={t} pendingOnly />
          ))}
        </Section>
      )}

      {/* Active – doctor claimed, needs traveler response */}
      {active.length > 0 && (
        <Section
          title={t.cardxReqSectionNeedsResponse}
          count={active.length}
          highlight
        >
          {active.map((r) => (
            <RequestCard
              key={r.id}
              r={r}
              fmt={fmt}
              t={t}
              acting={!!acting[r.token]}
              onGrant={() => respond(r.token, "GRANTED")}
              onDeny={() => respond(r.token, "DENIED")}
            />
          ))}
        </Section>
      )}

      {/* Resolved */}
      {resolved.length > 0 && (
        <Section
          title={t.cardxReqSectionResolved}
          count={resolved.length}
          muted
        >
          {resolved.map((r) => (
            <RequestCard key={r.id} r={r} fmt={fmt} t={t} />
          ))}
        </Section>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  count: number;
  highlight?: boolean;
  muted?: boolean;
  children: React.ReactNode;
}> = ({ title, count, highlight, muted, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <h2
        className={`text-sm font-semibold ${muted ? "text-gray-400" : "text-gray-700"}`}
      >
        {title}
      </h2>
      <span
        className={`text-xs rounded-full px-2 py-0.5 font-medium ${
          highlight
            ? "bg-amber-100 text-amber-700"
            : "bg-gray-100 text-gray-500"
        }`}
      >
        {count}
      </span>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const RequestCard: React.FC<{
  r: MedAccessRequest;
  fmt: (iso: string) => string;
  t: Translation;
  pendingOnly?: boolean;
  acting?: boolean;
  onGrant?: () => void;
  onDeny?: () => void;
}> = ({ r, fmt, t, pendingOnly, acting, onGrant, onDeny }) => {
  const statusBadge = {
    PENDING: r.doctor_name
      ? { text: t.cardxReqAwaitingYou, cls: "bg-amber-100 text-amber-700" }
      : { text: t.cardxReqWaitingDoctor, cls: "bg-blue-100 text-blue-700" },
    GRANTED: {
      text: t.cardxReqGranted,
      cls: "bg-emerald-100 text-emerald-700",
    },
    DENIED: { text: t.cardxAccessDeniedTitle, cls: "bg-red-100 text-red-700" },
    REVOKED: { text: t.cardxStatusRevoked, cls: "bg-gray-100 text-gray-500" },
  }[r.consent_status];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Stethoscope className="w-4 h-4 text-blue-500" />
          </div>
          <div>
            <div className="font-semibold text-gray-900 text-sm">
              {r.doctor_name ?? t.cardxReqDoctorUnknown}
            </div>
            {r.doctor_email && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                <Mail className="w-3 h-3" />
                {r.doctor_email}
              </div>
            )}
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusBadge.cls}`}
        >
          {statusBadge.text}
        </span>
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {t.cardxReqRequested.replace("{date}", fmt(r.created_at))}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {t.cardxReqExpires.replace("{date}", fmt(r.expires_at))}
        </div>
        {r.accessed_at && (
          <div className="flex items-center gap-1 text-emerald-600">
            <CheckCircle2 className="w-3 h-3" />
            {t.cardxReqAccessed.replace("{date}", fmt(r.accessed_at))}
          </div>
        )}
      </div>

      {/* Action buttons – only for claimed+PENDING */}
      {!pendingOnly && r.consent_status === "PENDING" && r.doctor_name && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={onGrant}
            disabled={acting}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {acting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {t.cardxReqGrant}
          </button>
          <button
            onClick={onDeny}
            disabled={acting}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <XCircle className="w-4 h-4" />
            {t.cardxReqDeny}
          </button>
        </div>
      )}

      {r.consent_status === "GRANTED" && (
        <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t.cardxReqGrantedNote}
        </div>
      )}
    </div>
  );
};
